import { Box } from '@mui/material'
import { SchedulerTable } from './Scheduler.muiTable'
import { useNavigate } from 'react-router-dom'
import {
  useChangeAccessType,
  useCloneSchedule,
  useDeleteSchedule,
  usePermissions,
  useRenameSchedule,
  useSheduler,
} from './Scheduler.data'
import { SECTIONS } from './SchedulerInput.sections'
import { ScheduleNameDialog } from './ScheduleName.dialog'
import { SchedulerTableDataType } from './Scheduler.types'
import {
  canModifySchedule,
  canOpenInput,
  canOpenOutput,
} from './scheduleStatus'
import {
  PLAN_ACCESS_OPTIONS,
  PLAN_ACCESS_TYPE,
} from 'Common/Constants/Constants'
import { GridRowParams } from '@mui/x-data-grid-premium'
import { PlanAccessSelect } from 'Common/Components/PlanView.components'
import { useEffect, useState } from 'react'
import { ShowPlansOptionType } from 'Common/Components/Common.types'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import PublicIcon from '@mui/icons-material/Public'
import { getUserEmail } from 'Features/auth/auth'
import toast from 'react-hot-toast'
import { ConfigurationSettingsDialog } from './ConfigurationSettings.dialog'
import { SchedulerBrandBar } from './SchedulerBrandBar'
import {
  PillButton,
  SchDisplay,
  SchedulerThemeScope,
  TOKENS,
} from './SchedulerDesign'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import TuneIcon from '@mui/icons-material/Tune'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'

export const SchedulerPage = () => {
  const navigate = useNavigate()
  const { canManageUsers, canManageSor, canManageConfig } = usePermissions()
  const [showSheduleOptions, setShowSheduleOptions] = useState(
    PLAN_ACCESS_OPTIONS['Generated by me'] as ShowPlansOptionType
  )

  const [userEmail, setUserEmail] = useState<string | null>(null)
  const [configSettingsOpen, setConfigSettingsOpen] = useState(false)

  const {
    data,
    isLoading: isFirstLoad,
    isFetchedAfterMount,
    refetch,
  } = useSheduler()
  // Only a refresh the user asked for shows on the button; the 30s
  // background refresh would otherwise flicker it every half minute.
  const [isRefreshing, setIsRefreshing] = useState(false)
  const handleRefresh = () => {
    setIsRefreshing(true)
    refetch().finally(() => setIsRefreshing(false))
  }
  // The cached list from an earlier visit can carry old statuses (a run
  // shown RUNNING that has since finished), so show the table as loading
  // until this visit's own fetch completes rather than displaying them.
  const isLoading = isFirstLoad || !isFetchedAfterMount

  const [tableData, setTableData] = useState<SchedulerTableDataType[]>(data)

  /** Clicking a row: a schedule that has run opens on its output (the
   *  Factory schedule), anything else on its input requirements. */
  const handleRowClick = (fullRow: GridRowParams<SchedulerTableDataType>) => {
    if (canOpenOutput(fullRow.row.status)) {
      handleOutputClick(fullRow)
      return
    }
    handleInputClick(fullRow)
  }

  const handleInputClick = (fullRow: GridRowParams<SchedulerTableDataType>) => {
    const { id, row } = fullRow
    // RUNNING opens too: the input page reattaches to the run's progress.
    if (canOpenInput(row.status)) {
      const maxKey = getMaxVersion(row.versionDetails)
      navigate(
        `/plan-scheduler/input/${id}/${maxKey}?section=${SECTIONS.PRODUCTION_REQUIREMENT}`
      )
    }
  }

  const getMaxVersion = (versionDetails: Record<number, unknown>) => {
    const keys = Object.keys(versionDetails)
    //Find the max value of the keys
    return keys?.reduce((a, b) => Math.max(a, parseInt(b)), 0) ?? 0
  }

  const handleOutputClick = (
    fullRow: GridRowParams<SchedulerTableDataType>
  ) => {
    const { id, row } = fullRow
    const { versionDetails } = row
    const maxKey = getMaxVersion(versionDetails)

    // The Factory schedule on the schedule page. The old output page
    // (`/output/…`) is retired; its route now redirects here as well.
    if (canOpenOutput(row.status)) {
      navigate(
        `/plan-scheduler/input/${id}/${maxKey}?section=${SECTIONS.FACTORY_LAYOUT}`
      )
    }
  }

  useEffect(() => {
    if (userEmail === null) {
      getUserEmail().then((email: string | undefined) => {
        setUserEmail(email ? email.toLocaleLowerCase() : '')
      })
    }
  }, [])

  const handleShowSchedulesDropdown = (event: any) => {
    const { value } = event.target
    setShowSheduleOptions(value as ShowPlansOptionType)
  }

  useEffect(() => {
    if (!data) return
    if (showSheduleOptions === PLAN_ACCESS_OPTIONS['Generated by me']) {
      const filterdData = data?.filter((item: SchedulerTableDataType) => {
        return item.generatedBy === userEmail
      })
      setTableData(filterdData)
    } else if (
      showSheduleOptions === PLAN_ACCESS_OPTIONS['Not generated by me']
    ) {
      const filterdData = data?.filter((item: SchedulerTableDataType) => {
        return item.generatedBy !== userEmail
      })
      setTableData(filterdData)
    } else {
      setTableData(data)
    }
  }, [showSheduleOptions, data, userEmail])

  const onAccessChangeSuccess = () => {
    toast.success('Access type updated successfully')
  }

  const onAccessChangeError = () => {
    toast.error('Error updating access type')
  }
  const { mutate: updateSheduleAccess } = useChangeAccessType(
    onAccessChangeSuccess,
    onAccessChangeError
  )

  const handleAccessTypeChange = (
    fullRow: GridRowParams<SchedulerTableDataType>
  ) => {
    const { row } = fullRow
    if (row.generatedBy !== userEmail || !canModifySchedule(row.status)) {
      return
    }

    let newAccessType = PLAN_ACCESS_TYPE.PRIVATE
    if (row.scheduleAccess?.scheduleAccessType === PLAN_ACCESS_TYPE.PRIVATE) {
      newAccessType = PLAN_ACCESS_TYPE.PUBLIC
    }
    updateSheduleAccess({
      schedulerId: row.id,
      accessType: newAccessType as 'public' | 'private',
    })
  }

  const onDeleteSuccess = () => {
    toast.success('Schedule deleted successfully')
  }
  const onDeleteError = () => {
    toast.error('Error deleting schedule')
  }

  const { mutate: deleteSchedule } = useDeleteSchedule(
    onDeleteSuccess,
    onDeleteError
  )

  const handleDelete = (fullRow: GridRowParams<SchedulerTableDataType>) => {
    const { row } = fullRow
    if (!canModifySchedule(row.status)) {
      return
    }
    deleteSchedule({ schedulerId: row.id })
  }

  // Rename and Clone share one name dialog; `nameDialog` says which.
  const [nameDialog, setNameDialog] = useState<{
    mode: 'rename' | 'clone'
    row: SchedulerTableDataType
  } | null>(null)

  const { mutate: renameSchedule, isLoading: isRenaming } = useRenameSchedule(
    () => {
      setNameDialog(null)
      toast.success('Schedule renamed')
    },
    (message) => toast.error(message)
  )
  const { mutate: cloneSchedule, isLoading: isCloning } = useCloneSchedule(
    (created) => {
      setNameDialog(null)
      toast.success('Schedule cloned')
      // Open the copy, as creating a schedule does.
      navigate(
        `/plan-scheduler/input/${created.id}/${created.iteration}?section=${SECTIONS.PRODUCTION_REQUIREMENT}`
      )
    },
    (message) => toast.error(message)
  )

  const handleRename = (fullRow: GridRowParams<SchedulerTableDataType>) => {
    const { row } = fullRow
    if (row.generatedBy !== userEmail || !canModifySchedule(row.status)) return
    setNameDialog({ mode: 'rename', row })
  }
  const handleClone = (fullRow: GridRowParams<SchedulerTableDataType>) => {
    if (!canModifySchedule(fullRow.row.status)) return
    setNameDialog({ mode: 'clone', row: fullRow.row })
  }

  return (
    <SchedulerThemeScope
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        // Full-bleed: /plan-scheduler renders bare (no global Planner
        // header/breadcrumb/footer — see Common/Layout Shell), so this
        // surface supplies its own chrome via SchedulerBrandBar below.
        background: TOKENS.surface50,
      }}
    >
      <SchedulerBrandBar />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          px: 3,
          py: 2.5,
        }}
      >
        <Box sx={{ mb: 2 }}>
          <SchDisplay>Schedules</SchDisplay>
          <Box sx={{ fontSize: 12, color: TOKENS.ink400, mt: 0.25 }}>
            All production schedules
          </Box>
        </Box>

        {/* The access filter on the left and the page actions on the right,
            on one row. Bottom-aligned: the filter carries its label above the
            box, so centring would sit the buttons against the label. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            flexWrap: 'wrap',
            gap: 2,
            mb: 2,
          }}
        >
          <PlanAccessSelect
            showPlansOption={showSheduleOptions}
            handleShowMyPlansDropdown={handleShowSchedulesDropdown}
            label="Schedule access"
          />
          <Box sx={{ flex: 1 }} />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {/* Fetches the list now rather than at the next 30s tick. The
                fetch also runs SchedulerService's sweep of stopped runs. */}
            <PillButton
              kind="ghost"
              icon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={isRefreshing}
              title="Fetch the latest schedule statuses"
            >
              {isRefreshing ? 'Refreshing…' : 'Refresh'}
            </PillButton>
            {/* Super User and above (deck slide 3). */}
            {/* Everyone sees this — it is the whole product for a Viewer,
                and a User needs to know what is in force. */}
            <PillButton
              kind="ghost"
              icon={<PublicIcon />}
              onClick={() => navigate('/plan-scheduler/published')}
            >
              Published schedules
            </PillButton>
            {canManageConfig && (
              <PillButton
                kind="ghost"
                icon={<TuneIcon />}
                onClick={() => setConfigSettingsOpen(true)}
              >
                Configuration settings
              </PillButton>
            )}
            {canManageSor && (
              <PillButton
                kind="ghost"
                icon={<Inventory2OutlinedIcon />}
                onClick={() => navigate('/plan-scheduler/system-of-records')}
              >
                System of records
              </PillButton>
            )}
            {/* Admins only. Rendered from the permission rather than the role
                name so it follows `config.ROLES` in SchedulerService instead
                of a second list here that could disagree with it.
                `usePermissions` defaults `canManageUsers` to FALSE while the
                answer is unknown — the opposite of its other flags, which
                default permissive so a slow /me cannot lock a scheduler out
                of their own tables. Hiding a button nobody but an Admin
                should see costs nothing if we are briefly wrong; showing it
                does. */}
            {canManageUsers && (
              <PillButton
                kind="ghost"
                icon={<GroupOutlinedIcon />}
                onClick={() => navigate('/plan-scheduler/manage-users')}
              >
                Manage users
              </PillButton>
            )}
            <PillButton
              kind="primary"
              icon={<AddIcon />}
              onClick={() => navigate('/plan-scheduler/create')}
            >
              Create schedule
            </PillButton>
          </Box>
        </Box>

        <SchedulerTable
          data={tableData}
          isLoading={isLoading}
          handleRowClick={handleRowClick}
          handleInputClick={handleInputClick}
          handleOutputClick={handleOutputClick}
          userEmail={userEmail as string}
          handleAccessTypeChange={handleAccessTypeChange}
          handleDelete={handleDelete}
          handleRename={handleRename}
          handleClone={handleClone}
        />
      </Box>

      <ScheduleNameDialog
        open={nameDialog !== null}
        title={
          nameDialog?.mode === 'clone' ? 'Clone schedule' : 'Rename schedule'
        }
        confirmLabel={nameDialog?.mode === 'clone' ? 'Clone' : 'Rename'}
        initialName={
          nameDialog
            ? nameDialog.mode === 'clone'
              ? `${nameDialog.row.name} (copy)`
              : nameDialog.row.name
            : ''
        }
        busy={isRenaming || isCloning}
        onClose={() => setNameDialog(null)}
        onConfirm={(name) => {
          if (!nameDialog) return
          const vars = { schedulerId: String(nameDialog.row.id), name }
          if (nameDialog.mode === 'clone') cloneSchedule(vars)
          else renameSchedule(vars)
        }}
      />

      <ConfigurationSettingsDialog
        open={configSettingsOpen}
        onClose={() => setConfigSettingsOpen(false)}
      />
    </SchedulerThemeScope>
  )
}
